import { useEffect, useRef, useState } from 'react';
import {
  GripVertical,
  ChevronUp,
  ChevronDown,
  Pencil,
  Trash2,
  Copy,
} from 'lucide-react';
import { IconButton, Checkbox } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import type { LiveProgram } from '../shared';
import {
  formatDurationHuman,
  scheduleStatus,
  scheduleTypeColor,
  scheduleTypeLabel,
  SCHEDULE_STATUS_TONE,
  SCHEDULE_STATUS_LABEL,
} from '../shared';
import type { Schedule } from '@/api/fastchannel';
import styles from '../Fastchannel.module.css';

interface PlaylistEditorProps {
  /** Schedules in their current playlist order (already ordered by the caller). */
  rows: Schedule[];
  live: LiveProgram | null;
  selected: Set<string>;
  busy: boolean;
  onToggleSelect: (id: string) => void;
  onEdit: (s: Schedule) => void;
  onDuplicate: (s: Schedule) => void;
  onDelete: (s: Schedule) => void;
  /** Persist a new id order. Called on drop / keyboard move when it changes. */
  onReorder: (orderedIds: string[]) => void;
}

/** Move the item at `from` to `to`, returning a new array (no mutation). */
function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) {
    return arr;
  }
  const next = arr.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/**
 * Drag-reorderable playlist for order-driven playout modes (loop/once/shuffle).
 * Order is local state seeded from `rows`; on drop (or keyboard move) it calls
 * `onReorder` with the new id order. Native HTML5 drag-and-drop — no extra dep.
 * Keyboard fallback: focus a row's handle and use Arrow Up/Down to move it.
 */
export function PlaylistEditor({
  rows,
  live,
  selected,
  busy,
  onToggleSelect,
  onEdit,
  onDuplicate,
  onDelete,
  onReorder,
}: PlaylistEditorProps) {
  // Local order so a drag re-renders instantly; re-seed when the server list
  // changes (add/remove/refetch). Compare by id sequence to avoid clobbering an
  // in-flight optimistic order with an identical refetch.
  const [order, setOrder] = useState<Schedule[]>(rows);
  const lastKey = useRef('');
  useEffect(() => {
    const key = rows.map((r) => r.id).join('|');
    if (key !== lastKey.current) {
      lastKey.current = key;
      setOrder(rows);
    }
  }, [rows]);

  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const commit = (next: Schedule[]) => {
    setOrder(next);
    onReorder(next.map((s) => s.id));
  };

  const handleDrop = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const from = order.findIndex((s) => s.id === dragId);
    const to = order.findIndex((s) => s.id === targetId);
    const next = moveItem(order, from, to);
    if (next !== order) commit(next);
    setDragId(null);
    setOverId(null);
  };

  const moveBy = (id: string, delta: number) => {
    const from = order.findIndex((s) => s.id === id);
    const next = moveItem(order, from, from + delta);
    if (next !== order) commit(next);
  };

  return (
    <ol className={styles.scheduleList} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {order.map((s, i) => {
        const status = scheduleStatus(s);
        const isSel = selected.has(s.id);
        const isLive = live?.program.id === s.id;
        const isDragging = dragId === s.id;
        const isOver = overId === s.id && dragId !== s.id;
        const cls = [
          styles.scheduleRow,
          status === 'expired' && !isLive ? styles.scheduleRowExpired : '',
          isSel ? styles.rowSelected : '',
          isDragging ? styles.scheduleRowDragging : '',
          isOver ? styles.scheduleRowOver : '',
        ]
          .filter(Boolean)
          .join(' ');
        const rowStyle = isLive
          ? { borderLeftColor: 'var(--vn-success-solid)', background: 'var(--vn-success-subtle)' }
          : { borderLeftColor: scheduleTypeColor(s.type) };

        return (
          <li
            key={s.id}
            className={cls}
            style={rowStyle}
            onDragOver={(e) => {
              if (!dragId) return;
              e.preventDefault();
              setOverId(s.id);
            }}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop(s.id);
            }}
          >
            <span
              className={styles.dragHandle}
              draggable
              role="button"
              tabIndex={0}
              aria-label={`Reorder ${s.title || 'program'}. Use arrow up or down to move.`}
              onDragStart={() => setDragId(s.id)}
              onDragEnd={() => {
                setDragId(null);
                setOverId(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  moveBy(s.id, -1);
                } else if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  moveBy(s.id, 1);
                }
              }}
            >
              <GripVertical size={16} />
            </span>
            <Checkbox
              checked={isSel}
              onCheckedChange={() => onToggleSelect(s.id)}
              aria-label={`Select ${s.title || 'program'}`}
            />
            <span className={styles.scheduleTime} style={{ minWidth: 32, textAlign: 'center' }}>
              {i + 1}
            </span>
            <div className={styles.scheduleBody}>
              <span className={styles.scheduleTitle}>{s.title || 'Untitled program'}</span>
              <span className={styles.scheduleSub}>
                {scheduleTypeLabel(s.type)} · {formatDurationHuman(s.duration)}
                {s.description ? ` · ${s.description}` : ''}
              </span>
            </div>
            {isLive ? (
              <StatusPill tone="success">On air</StatusPill>
            ) : (
              <StatusPill tone={SCHEDULE_STATUS_TONE[status]}>{SCHEDULE_STATUS_LABEL[status]}</StatusPill>
            )}
            <div className={styles.scheduleActions}>
              <IconButton
                variant="ghost"
                size="sm"
                aria-label="Move up"
                disabled={i === 0 || busy}
                onClick={() => moveBy(s.id, -1)}
              >
                <ChevronUp size={14} />
              </IconButton>
              <IconButton
                variant="ghost"
                size="sm"
                aria-label="Move down"
                disabled={i === order.length - 1 || busy}
                onClick={() => moveBy(s.id, 1)}
              >
                <ChevronDown size={14} />
              </IconButton>
              <IconButton
                variant="ghost"
                size="sm"
                aria-label="Duplicate program"
                disabled={busy}
                onClick={() => onDuplicate(s)}
              >
                <Copy size={14} />
              </IconButton>
              <IconButton
                variant="ghost"
                size="sm"
                aria-label="Edit program"
                onClick={() => onEdit(s)}
              >
                <Pencil size={14} />
              </IconButton>
              <IconButton
                variant="ghost"
                size="sm"
                aria-label="Delete program"
                onClick={() => onDelete(s)}
              >
                <Trash2 size={14} />
              </IconButton>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
